import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from 'sonner';
import { ArrowLeft, Building2, DollarSign, TrendingUp, Users, Clock, FileText, Receipt as ReceiptIcon, Download, ExternalLink, Check, X, Calendar as CalendarIcon } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import ExpenseManagement from '../components/ExpenseManagement';
import FileAttachments from '../components/FileAttachments';
import ProjectTimeEntries from '../components/ProjectTimeEntries';
import { formatCurrency } from '../utils/formatters';

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [expenses, setExpenses] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [financialData, setFinancialData] = useState(null);
  const [chartView, setChartView] = useState('dollars'); // 'dollars' or 'percent'
  const [projectServices, setProjectServices] = useState([]);
  const [invoiceUnitsModalOpen, setInvoiceUnitsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [unitsToBill, setUnitsToBill] = useState(0);
  const [expandedServices, setExpandedServices] = useState([]);
  const [serviceAssignments, setServiceAssignments] = useState({});

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  useEffect(() => {
    // Update active tab when URL changes
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  useEffect(() => {
    // Fetch financial data when financials tab is active
    if (activeTab === 'financials' && projectId) {
      fetchFinancialData();
    }
    // Fetch project services when phasing tab is active
    if (activeTab === 'phasing' && projectId) {
      fetchProjectServices();
    }
  }, [activeTab, projectId]);

  const fetchProjectData = async () => {
    try {
      setLoading(true);
      const [projectRes, expensesRes, proposalsRes, invoicesRes] = await Promise.all([
        api.get(`/projects`),
        api.get(`/expenses`),
        api.get(`/proposals`),
        api.get(`/invoices`)
      ]);
      
      // Find project from the list
      const projectData = Array.isArray(projectRes.data) 
        ? projectRes.data.find(p => p.id === projectId)
        : projectRes.data;
        
      setProject(projectData);
      
      // Filter data for this project
      setExpenses(expensesRes.data.filter(e => e.project_name === projectData?.project_name));
      
      // Find proposals linked to this project
      const linkedProposals = proposalsRes.data.filter(p => p.converted_to_project_id === projectId);
      setProposals(linkedProposals);
      
      // Find invoices for this project (by project name match)
      const linkedInvoices = invoicesRes.data.filter(inv => 
        inv.project_name === projectData?.project_name || 
        linkedProposals.some(prop => prop.converted_to_invoice_id === inv.id)
      );
      setInvoices(linkedInvoices);
    } catch (error) {
      console.error('Error fetching project data:', error);
      toast.error('Failed to load project details');
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancialData = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/financial-summary`);
      setFinancialData(response.data);
    } catch (error) {
      console.error('Error fetching financial data:', error);
      toast.error('Failed to load financial data');
    }
  };

  const fetchProjectServices = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/services`);
      setProjectServices(response.data || []);
      
      // Fetch assignments for each service
      for (const service of response.data || []) {
        fetchServiceAssignments(service.id);
      }
    } catch (error) {
      console.error('Error fetching project services:', error);
      toast.error('Failed to load project services');
    }
  };

  const fetchServiceAssignments = async (serviceId) => {
    try {
      const response = await api.get(`/projects/${projectId}/services/${serviceId}/assignments`);
      setServiceAssignments(prev => ({
        ...prev,
        [serviceId]: response.data || []
      }));
    } catch (error) {
      console.error('Error fetching service assignments:', error);
    }
  };

  const toggleServiceExpand = (serviceId) => {
    setExpandedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleInvoiceUnits = (service) => {
    setSelectedService(service);
    setUnitsToBill(Math.min(5, service.remaining_units)); // Default to 5 or max remaining
    setInvoiceUnitsModalOpen(true);
  };

  const handleCreatePartialInvoice = async () => {
    if (!selectedService || unitsToBill <= 0) return;

    try {
      const response = await api.post(`/projects/${projectId}/invoice-units`, {
        project_service_id: selectedService.id,
        units_to_bill: unitsToBill,
        notes: null
      });

      toast.success(response.data.message);
      setInvoiceUnitsModalOpen(false);
      fetchProjectServices();
      fetchProjectData(); // Refresh to show new invoice
    } catch (error) {
      console.error('Error creating partial invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleTogglePayoutStatus = async (partnerId, currentStatus) => {
    try {
      await api.put(`/projects/${projectId}/partners/${partnerId}/payout`, null, {
        params: { paid: !currentStatus }
      });
      toast.success('Payout status updated');
      fetchFinancialData(); // Refresh data
    } catch (error) {
      console.error('Error updating payout status:', error);
      toast.error('Failed to update payout status');
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/financial-report/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `project_financial_report_${projectId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF report downloaded');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#247C75]"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Project Not Found</h2>
          <Button onClick={() => navigate('/projects')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'phasing', label: 'Phasing & Billing' },
    { id: 'team', label: 'Team & Time' },
    { id: 'financials', label: 'Financials' },
    { id: 'expenses', label: 'Expenses' },
    { id: 'files', label: 'Files' }
  ];

  const totalRevenue = project.revenue || 0;
  const hst = project.hst || 0;
  const hstAmount = totalRevenue * (hst / 100);
  const totalWithHST = totalRevenue + hstAmount;

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/projects')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Projects
        </Button>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              {project.project_name}
            </h1>
            <div className="flex items-center gap-3">
              <Badge 
                variant="outline"
                className={
                  project.status === 'completed' 
                    ? 'border-green-300 bg-green-50 text-green-700'
                    : 'border-blue-300 bg-blue-50 text-blue-700'
                }
              >
                {project.status || 'Active'}
              </Badge>
              {project.size_sqft && (
                <span className="text-sm text-slate-600">
                  {project.size_sqft.toLocaleString()} sq ft
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => navigate(`/ops-manager?tab=schedule&project=${projectId}`)}
              className="bg-[#247C75] hover:bg-[#1d6259]"
            >
              <Users className="h-4 w-4 mr-2" />
              Plan in Ops Manager
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="flex gap-8">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                py-3 px-1 border-b-2 font-medium text-sm transition-colors
                ${activeTab === tab.id 
                  ? 'border-[#247C75] text-[#247C75]' 
                  : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Revenue</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">
                      ${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <DollarSign className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">HST ({hst}%)</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">
                      ${hstAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Total w/ HST</p>
                    <p className="text-2xl font-bold text-emerald-600 mt-1">
                      ${totalWithHST.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="bg-emerald-50 p-3 rounded-lg">
                    <DollarSign className="h-6 w-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Status</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">
                      {project.status || 'Active'}
                    </p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <Building2 className="h-6 w-6 text-slate-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Project Info */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle>Project Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-600">Size</Label>
                  <p className="text-base text-slate-900 mt-1">
                    {project.size_sqft ? `${project.size_sqft.toLocaleString()} sq ft` : 'Not specified'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-600">Measurement Type</Label>
                  <p className="text-base text-slate-900 mt-1">
                    {project.measurement_type || 'Fixed'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'team' && (
        <ProjectTimeEntries 
          projectId={projectId} 
          projectName={project.project_name}
        />
      )}

      {activeTab === 'financials' && (
        <div className="space-y-6">
          {/* Estimated vs Actual Comparison */}
          {(project.estimated_labour_hours || project.estimated_labour_cost) && (
            <Card className="border-blue-200 bg-blue-50/30 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-blue-600" />
                  Estimated vs Actual Comparison
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Labour Hours Comparison */}
                  {project.estimated_labour_hours && (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-slate-900">Labour Hours</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-600">Estimated:</span>
                          <span className="font-semibold">{project.estimated_labour_hours}h</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-600">Actual:</span>
                          <span className="font-semibold">{financialData?.summary?.total_hours || 0}h</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t">
                          <span className="text-sm font-medium text-slate-900">Variance:</span>
                          <span className={`font-bold ${
                            (financialData?.summary?.total_hours || 0) > project.estimated_labour_hours 
                              ? 'text-red-600' 
                              : 'text-green-600'
                          }`}>
                            {((financialData?.summary?.total_hours || 0) - project.estimated_labour_hours).toFixed(1)}h
                            {' '}
                            ({project.estimated_labour_hours > 0 
                              ? (((financialData?.summary?.total_hours || 0) - project.estimated_labour_hours) / project.estimated_labour_hours * 100).toFixed(1)
                              : 0}%)
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Labour Cost Comparison */}
                  {project.estimated_labour_cost && (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-slate-900">Labour Cost</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-600">Estimated:</span>
                          <span className="font-semibold">{formatCurrency(project.estimated_labour_cost)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-600">Actual:</span>
                          <span className="font-semibold">{formatCurrency(financialData?.breakdown?.labour || 0)}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t">
                          <span className="text-sm font-medium text-slate-900">Variance:</span>
                          <span className={`font-bold ${
                            (financialData?.breakdown?.labour || 0) > project.estimated_labour_cost 
                              ? 'text-red-600' 
                              : 'text-green-600'
                          }`}>
                            {formatCurrency((financialData?.breakdown?.labour || 0) - project.estimated_labour_cost)}
                            {' '}
                            ({project.estimated_labour_cost > 0
                              ? (((financialData?.breakdown?.labour || 0) - project.estimated_labour_cost) / project.estimated_labour_cost * 100).toFixed(1)
                              : 0}%)
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Accuracy Indicator */}
                {project.estimated_labour_hours && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">Estimation Accuracy:</span>
                      <Badge className={
                        Math.abs(((financialData?.summary?.total_hours || 0) - project.estimated_labour_hours) / project.estimated_labour_hours * 100) <= 10
                          ? 'bg-green-100 text-green-700 border-green-300'
                          : Math.abs(((financialData?.summary?.total_hours || 0) - project.estimated_labour_hours) / project.estimated_labour_hours * 100) <= 25
                          ? 'bg-yellow-100 text-yellow-700 border-yellow-300'
                          : 'bg-red-100 text-red-700 border-red-300'
                      }>
                        {Math.abs(((financialData?.summary?.total_hours || 0) - project.estimated_labour_hours) / project.estimated_labour_hours * 100) <= 10
                          ? 'Excellent (±10%)'
                          : Math.abs(((financialData?.summary?.total_hours || 0) - project.estimated_labour_hours) / project.estimated_labour_hours * 100) <= 25
                          ? 'Good (±25%)'
                          : 'Needs Improvement (>25%)'}
                      </Badge>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Top Summary Cards */}
          {financialData && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* Total Revenue */}
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600">Total Revenue</p>
                        <p className="text-2xl font-bold text-[#247C75] mt-1">
                          {formatCurrency(financialData.summary.total_revenue)}
                        </p>
                        {financialData.summary.invoice_revenue !== financialData.summary.total_revenue && (
                          <p className="text-xs text-slate-500 mt-1">
                            Invoiced: {formatCurrency(financialData.summary.invoice_revenue)}
                          </p>
                        )}
                      </div>
                      <div className="bg-[#247C75] bg-opacity-10 p-3 rounded-lg">
                        <DollarSign className="h-6 w-6 text-[#247C75]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Total Cost */}
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600">Total Cost</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1">
                          {formatCurrency(financialData.summary.total_cost)}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Labour + Expenses
                        </p>
                      </div>
                      <div className="bg-slate-100 p-3 rounded-lg">
                        <TrendingUp className="h-6 w-6 text-slate-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Gross Profit */}
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600">Gross Profit</p>
                        <p className={`text-2xl font-bold mt-1 ${
                          financialData.summary.gross_profit >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatCurrency(financialData.summary.gross_profit)}
                        </p>
                      </div>
                      <div className={`p-3 rounded-lg ${
                        financialData.summary.gross_profit >= 0 ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        <DollarSign className={`h-6 w-6 ${
                          financialData.summary.gross_profit >= 0 ? 'text-green-600' : 'text-red-600'
                        }`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Profit Margin % */}
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600">Profit Margin</p>
                        <p className={`text-2xl font-bold mt-1 ${
                          financialData.summary.profit_margin_pct >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {financialData.summary.profit_margin_pct.toFixed(2)}%
                        </p>
                      </div>
                      <div className={`p-3 rounded-lg ${
                        financialData.summary.profit_margin_pct >= 0 ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        <TrendingUp className={`h-6 w-6 ${
                          financialData.summary.profit_margin_pct >= 0 ? 'text-green-600' : 'text-red-600'
                        }`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Profitability Chart */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Profitability Breakdown</CardTitle>
                      <CardDescription>Visual breakdown of revenue and costs</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant={chartView === 'dollars' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setChartView('dollars')}
                        className={chartView === 'dollars' ? 'bg-[#247C75] hover:bg-[#1f6b65]' : ''}
                      >
                        $
                      </Button>
                      <Button
                        variant={chartView === 'percent' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setChartView('percent')}
                        className={chartView === 'percent' ? 'bg-[#247C75] hover:bg-[#1f6b65]' : ''}
                      >
                        %
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={(() => {
                            const revenue = financialData.breakdown.revenue || 1; // Prevent division by zero
                            const labour = financialData.breakdown.labour || 0;
                            const expenses = financialData.breakdown.expenses || 0;
                            const profit = Math.max(0, financialData.breakdown.profit || 0);
                            
                            if (chartView === 'dollars') {
                              return [
                                { name: 'Labour Cost', value: labour },
                                { name: 'Expenses', value: expenses },
                                { name: 'Profit', value: profit }
                              ];
                            } else {
                              return [
                                { 
                                  name: 'Labour Cost', 
                                  value: revenue > 0 ? (labour / revenue * 100) : 0
                                },
                                { 
                                  name: 'Expenses', 
                                  value: revenue > 0 ? (expenses / revenue * 100) : 0
                                },
                                { 
                                  name: 'Profit', 
                                  value: revenue > 0 ? (profit / revenue * 100) : 0
                                }
                              ];
                            }
                          })()}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          fill="#8884d8"
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, value }) => 
                            chartView === 'dollars' 
                              ? `${name}: ${formatCurrency(value)}`
                              : `${name}: ${value.toFixed(1)}%`
                          }
                        >
                          <Cell key="cell-0" fill="#ef4444" />
                          <Cell key="cell-1" fill="#f59e0b" />
                          <Cell key="cell-2" fill="#10b981" />
                        </Pie>
                        <Tooltip 
                          formatter={(value) => 
                            chartView === 'dollars' 
                              ? formatCurrency(value) 
                              : `${value.toFixed(2)}%`
                          }
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Partner Payout Table */}
              {financialData.partner_payouts && financialData.partner_payouts.length > 0 && (
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle>Partner Payout Summary</CardTitle>
                    <CardDescription>Profit sharing and payout tracking</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Partner</TableHead>
                          <TableHead className="text-right">Profit Share %</TableHead>
                          <TableHead className="text-right">Profit Share Amount</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                          <TableHead className="text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {financialData.partner_payouts.map((payout) => (
                          <TableRow key={payout.partner_id}>
                            <TableCell className="font-medium">{payout.partner_name}</TableCell>
                            <TableCell className="text-right">
                              {payout.profit_share_pct.toFixed(2)}%
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(payout.profit_share_amount)}
                            </TableCell>
                            <TableCell className="text-center">
                              {payout.paid ? (
                                <Badge className="bg-green-100 text-green-800">
                                  Paid {payout.paid_at ? `• ${new Date(payout.paid_at).toLocaleDateString()}` : ''}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="border-orange-200 text-orange-800">
                                  Unpaid
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleTogglePayoutStatus(payout.partner_id, payout.paid)}
                                className="h-8"
                              >
                                {payout.paid ? (
                                  <>
                                    <X className="h-4 w-4 mr-1" />
                                    Mark Unpaid
                                  </>
                                ) : (
                                  <>
                                    <Check className="h-4 w-4 mr-1" />
                                    Mark Paid
                                  </>
                                )}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-slate-50 font-semibold">
                          <TableCell colSpan={2}>Total Partner Payout</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(financialData.summary.total_partner_payout)}
                          </TableCell>
                          <TableCell colSpan={2}></TableCell>
                        </TableRow>
                        <TableRow className="bg-[#e8f5f4] font-semibold">
                          <TableCell colSpan={2}>Net Profit After Partner Payout</TableCell>
                          <TableCell className={`text-right ${
                            financialData.summary.net_profit >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {formatCurrency(financialData.summary.net_profit)}
                          </TableCell>
                          <TableCell colSpan={2}></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Export Button */}
              <div className="flex justify-end">
                <Button onClick={handleExportPDF} className="bg-[#247C75] hover:bg-[#1f6b65]">
                  <Download className="h-4 w-4 mr-2" />
                  Export PDF Report
                </Button>
              </div>
            </>
          )}

          {!financialData && (
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#247C75] mx-auto mb-4"></div>
                <p className="text-slate-500">Loading financial data...</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'expenses' && (
        <div>
          <ExpenseManagement 
            expenses={expenses.filter(e => e.project_name === project.project_name)}
            projects={[project]}
            onUpdate={fetchProjectData}
          />
        </div>
      )}

      {/* Phasing & Billing Tab */}
      {activeTab === 'phasing' && (
        <div className="space-y-6">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ReceiptIcon className="h-5 w-5 text-[#247C75]" />
                Project Services - Phased Billing
              </CardTitle>
              <CardDescription>
                Track unit-based services and create partial invoices
              </CardDescription>
            </CardHeader>
            <CardContent>
              {projectServices.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Service Name</TableHead>
                      <TableHead className="text-center">Total Units</TableHead>
                      <TableHead className="text-center">Billed Units</TableHead>
                      <TableHead className="text-center">Remaining Units</TableHead>
                      <TableHead className="text-right">Total Value</TableHead>
                      <TableHead className="text-right">Billed to Date</TableHead>
                      <TableHead className="text-right">Remaining Value</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projectServices.map((service) => {
                      const billedValue = service.billed_units * service.unit_rate;
                      const remainingValue = service.remaining_units * service.unit_rate;
                      const isExpanded = expandedServices.includes(service.id);
                      const assignments = serviceAssignments[service.id] || [];
                      
                      return (
                        <React.Fragment key={service.id}>
                          <TableRow className="hover:bg-slate-50">
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => toggleServiceExpand(service.id)}
                                  className="text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                  {isExpanded ? '▼' : '▶'}
                                </button>
                                <div>
                                  <div className="text-sm text-slate-900">{service.name}</div>
                                  <div className="text-xs text-slate-500">
                                    ${service.unit_rate.toFixed(2)} per {service.unit_label}
                                  </div>
                                  {assignments.length > 0 && (
                                    <div className="flex items-center gap-1 mt-1">
                                      <CalendarIcon className="h-3 w-3 text-purple-600" />
                                      <span className="text-xs text-purple-600 font-medium">
                                        {assignments.length} scheduled phase{assignments.length !== 1 ? 's' : ''}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="bg-slate-50">
                                {service.total_units} {service.unit_label}s
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                {service.billed_units}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                {service.remaining_units}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              ${service.total_value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </TableCell>
                            <TableCell className="text-right text-green-700 font-medium">
                              ${billedValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </TableCell>
                            <TableCell className="text-right text-blue-700 font-medium">
                              ${remainingValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                onClick={() => handleInvoiceUnits(service)}
                                disabled={service.remaining_units === 0}
                                className="bg-[#247C75] hover:bg-[#1d6259] text-white"
                              >
                                <ReceiptIcon className="h-3 w-3 mr-1" />
                                Invoice Units
                              </Button>
                            </TableCell>
                          </TableRow>
                          
                          {/* Expandable Scheduled Work Section */}
                          {isExpanded && assignments.length > 0 && (
                            <TableRow>
                              <TableCell colSpan={8} className="bg-slate-50/50 p-0">
                                <div className="p-4 border-l-4 border-purple-400">
                                  <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                                    <CalendarIcon className="h-4 w-4 text-purple-600" />
                                    Scheduled Work for this Service
                                  </h4>
                                  <div className="space-y-2">
                                    {assignments.map((phase, idx) => (
                                      <div key={idx} className="bg-white p-3 rounded-lg border border-slate-200">
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="flex items-center gap-3">
                                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                              {phase.phase_label}
                                            </Badge>
                                            <span className="text-sm text-slate-600">
                                              {new Date(phase.date).toLocaleDateString('en-US', { 
                                                weekday: 'short', 
                                                month: 'short', 
                                                day: 'numeric' 
                                              })}
                                            </span>
                                          </div>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => navigate(`/ops-manager?tab=schedule&date=${phase.date}`)}
                                            className="text-xs"
                                          >
                                            Open in Ops Manager →
                                          </Button>
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                          {phase.assignments.map((assignment, aidx) => (
                                            <div key={aidx} className="flex items-center gap-2 text-xs">
                                              <Users className="h-3 w-3 text-slate-400" />
                                              <span className="text-slate-700">Employee ID: {assignment.employee_id}</span>
                                              <Badge variant="outline" className={`text-xs ${
                                                assignment.status === 'completed' ? 'bg-green-50 text-green-700' :
                                                assignment.status === 'confirmed' ? 'bg-blue-50 text-blue-700' :
                                                'bg-slate-50 text-slate-600'
                                              }`}>
                                                {assignment.status}
                                              </Badge>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <ReceiptIcon className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-sm">No project services found</p>
                  <p className="text-xs mt-1">Services are automatically created when a proposal is accepted</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'files' && (
        <div className="space-y-6">
          {/* Proposals Section */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Proposals
              </CardTitle>
              <CardDescription>
                {proposals.length > 0 
                  ? `${proposals.length} proposal(s) linked to this project`
                  : 'No proposals linked to this project yet'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {proposals.length > 0 ? (
                <div className="space-y-3">
                  {proposals.map((proposal) => (
                    <div 
                      key={proposal.id}
                      className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <FileText className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{proposal.estimate_number}</p>
                          <p className="text-sm text-slate-600">
                            {proposal.prepared_for_name} • {new Date(proposal.estimate_date).toLocaleDateString()}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className={
                              proposal.status === 'accepted' ? 'border-green-300 bg-green-50 text-green-700' :
                              proposal.status === 'rejected' ? 'border-red-300 bg-red-50 text-red-700' :
                              'border-blue-300 bg-blue-50 text-blue-700'
                            }>
                              {proposal.status}
                            </Badge>
                            <span className="text-sm font-semibold text-slate-900">
                              ${proposal.total_estimate?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`/api/proposals/${proposal.id}/pdf`, '_blank')}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          PDF
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/proposals?id=${proposal.id}`)}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No proposals found for this project</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Invoices Section */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ReceiptIcon className="h-5 w-5 text-green-600" />
                Invoices
              </CardTitle>
              <CardDescription>
                {invoices.length > 0 
                  ? `${invoices.length} invoice(s) linked to this project`
                  : 'No invoices linked to this project yet'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {invoices.length > 0 ? (
                <div className="space-y-3">
                  {invoices.map((invoice) => (
                    <div 
                      key={invoice.id}
                      className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-green-100 p-2 rounded-lg">
                          <ReceiptIcon className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{invoice.invoice_number}</p>
                          <p className="text-sm text-slate-600">
                            {invoice.client_name} • {new Date(invoice.invoice_date).toLocaleDateString()}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className={
                              invoice.status === 'paid' ? 'border-green-300 bg-green-50 text-green-700' :
                              invoice.status === 'overdue' ? 'border-red-300 bg-red-50 text-red-700' :
                              invoice.status === 'partially_paid' ? 'border-orange-300 bg-orange-50 text-orange-700' :
                              'border-blue-300 bg-blue-50 text-blue-700'
                            }>
                              {invoice.status?.replace('_', ' ')}
                            </Badge>
                            <span className="text-sm font-semibold text-slate-900">
                              ${invoice.total?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`/api/invoices/${invoice.id}/pdf`, '_blank')}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          PDF
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/invoices?id=${invoice.id}`)}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <ReceiptIcon className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No invoices found for this project</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* File Attachments Section */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle>Project Files & Photos</CardTitle>
              <CardDescription>Upload before/after photos, documents, and other files</CardDescription>
            </CardHeader>
            <CardContent>
              <FileAttachments 
                entityType="project"
                entityId={projectId}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Invoice Units Modal */}
      <Dialog open={invoiceUnitsModalOpen} onOpenChange={setInvoiceUnitsModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invoice Units - Partial Billing</DialogTitle>
          </DialogHeader>
          
          {selectedService && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-2">{selectedService.name}</h3>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-slate-600">Total Units</div>
                    <div className="font-semibold text-slate-900">{selectedService.total_units}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-600">Billed</div>
                    <div className="font-semibold text-green-700">{selectedService.billed_units}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-600">Remaining</div>
                    <div className="font-semibold text-blue-700">{selectedService.remaining_units}</div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Units to Bill Now</Label>
                <Input
                  type="number"
                  value={unitsToBill}
                  onChange={(e) => setUnitsToBill(Math.min(selectedService.remaining_units, Math.max(0, parseInt(e.target.value) || 0)))}
                  min={0}
                  max={selectedService.remaining_units}
                  className="text-lg font-semibold"
                />
                <p className="text-xs text-slate-500">
                  Maximum: {selectedService.remaining_units} {selectedService.unit_label}(s)
                </p>
              </div>

              <div className="bg-[#247C75]/10 p-4 rounded-lg border border-[#247C75]/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-700">Unit Rate:</span>
                  <span className="font-semibold text-slate-900">
                    ${selectedService.unit_rate.toFixed(2)} per {selectedService.unit_label}
                  </span>
                </div>
                <div className="flex items-center justify-between text-lg">
                  <span className="font-semibold text-slate-900">Invoice Amount:</span>
                  <span className="font-bold text-[#247C75]">
                    ${(unitsToBill * selectedService.unit_rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <p className="text-xs text-slate-600 mt-2">
                  Plus {project?.hst || 14}% HST = ${((unitsToBill * selectedService.unit_rate) * (1 + (project?.hst || 14) / 100)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceUnitsModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreatePartialInvoice}
              disabled={!unitsToBill || unitsToBill <= 0}
              className="bg-[#247C75] hover:bg-[#1d6259]"
            >
              <ReceiptIcon className="h-4 w-4 mr-2" />
              Create Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Remove the duplicate Label component definition
export default ProjectDetailPage;
